import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { TECH } from "@/data/content";
import { Reveal, LineReveal, Stagger, Item } from "./motion/Reveal";

const offsets = ["lg:mt-0", "lg:mt-16", "lg:mt-4", "lg:mt-24", "lg:mt-8", "lg:mt-20"];

export const Technology = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const x = useTransform(scrollYProgress, [0, 1], ["4%", "-12%"]);

  return (
    <section id="technology" data-testid="tech-section" ref={ref} className="section-y bg-charcoal text-ivory relative overflow-hidden">
      <motion.p style={{ x }} aria-hidden className="absolute top-8 left-0 whitespace-nowrap font-serif text-[18vw] leading-none text-ivory/[0.035] select-none pointer-events-none">
        HTML · CSS · JavaScript · Python · Java · AI
      </motion.p>
      <div className="container-x relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 mb-20 lg:mb-28">
          <div className="lg:col-span-6">
            <Reveal><p className="eyebrow !text-gold/80 mb-6">Technology</p></Reveal>
            <LineReveal inView as="h2" lines={["Built With", <>Modern <span className="italic text-ivory/60">Technology.</span></>]} className="h2 !text-ivory" />
          </div>
          <Reveal delay={0.2} className="lg:col-span-5 lg:col-start-8 text-base sm:text-lg leading-relaxed text-ivory/65 font-light lg:pt-4 space-y-5">
            <p>Technology is chosen to serve the project, never the other way around. Each website is built with the languages and tools best suited to its requirements, performance and future.</p>
            <p>Where appropriate, AI-assisted development tools support the process, improving efficiency and opening creative possibilities. The design thinking, customization and care behind every website remain unmistakably human.</p>
          </Reveal>
        </div>

        <Stagger className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 lg:gap-4">
          {TECH.map((t, i) => (
            <Item key={t.id} data-testid={`tech-card-${t.id}`} className={`group relative bg-charcoal border border-ivory/15 hover:border-ivory p-6 lg:p-7 min-h-[240px] lg:min-h-[300px] flex flex-col justify-between overflow-hidden transition-colors duration-500 ${offsets[i]}`}>
              <div className="absolute inset-0 bg-ivory translate-y-full group-hover:translate-y-0 transition-transform duration-700 ease-out" />
              <div className="relative flex items-center justify-between">
                <span className="font-mono text-[10px] tracking-[0.25em] text-ivory/50 group-hover:text-sage transition-colors duration-500">0{i + 1}</span>
                <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-gold/70 group-hover:text-taupe transition-colors duration-500">{t.role}</span>
              </div>
              <div className="relative">
                <h3 className="font-serif text-3xl lg:text-4xl font-light text-ivory group-hover:text-charcoal transition-colors duration-500 leading-none">{t.name}</h3>
                <p className="text-xs leading-relaxed text-ivory/0 group-hover:text-ink/75 mt-4 transition-colors duration-500 font-light">{t.desc}</p>
              </div>
            </Item>
          ))}
        </Stagger>
      </div>
    </section>
  );
};
